import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GameResultOverlay from './GameResultOverlay';
import useGameSounds from './useGameSounds';

const ZONES = ['head', 'body', 'legs'];
const INITIAL_HP = 100;
const ROUND_TIMER_SECONDS = 15;
const REVEAL_DURATION_MS = 2500;

function BattleRoom({ player, opponent, socket, bet, battleState, battleRound, battleEnd, opponentChose, setBattleRound, setBattleEnd }) {
    const { t } = useTranslation();
    const [chosen, setChosen] = useState(null);
    const [revealing, setRevealing] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(ROUND_TIMER_SECONDS);
    const [showResult, setShowResult] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const { playWin, playLose, playChoose, playDraw } = useGameSounds();
    const revealTimerRef = useRef(null);

    const myName = player.name;
    const opponentName = opponent;
    const isAttacker = battleState ? battleState.attackerName === myName : false;
    const myHp = battleState?.hp?.[myName] ?? INITIAL_HP;
    const oppHp = battleState?.hp?.[opponentName] ?? INITIAL_HP;

    // Handle round reveal animation
    useEffect(() => {
        if (!battleRound) return;
        setRevealing(true);
        setChosen(null);
        if (battleRound.blocked) {
            playDraw();
            if (navigator.vibrate) navigator.vibrate(40);
        } else {
            const iWasDefender = battleRound.defenderName === myName;
            if (iWasDefender) {
                if (navigator.vibrate) navigator.vibrate(120);
            }
        }
        if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
        revealTimerRef.current = setTimeout(() => {
            setRevealing(false);
            if (setBattleRound) setBattleRound(null);
        }, REVEAL_DURATION_MS);
        return () => {
            if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
        };
    }, [battleRound]); // eslint-disable-line

    // Handle battle end overlay
    useEffect(() => {
        if (!battleEnd) {
            setShowResult(false);
            setGameResult(null);
            return;
        }
        const iWon = battleEnd.winnerName === myName;
        setGameResult(iWon ? 'win' : 'lose');
        setShowResult(true);
        if (iWon) {
            playWin();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
        } else {
            playLose();
            if (navigator.vibrate) navigator.vibrate(200);
        }
    }, [battleEnd]); // eslint-disable-line

    // Countdown timer — reset on each new round
    useEffect(() => {
        if (!battleState || revealing || battleEnd) return;
        setSecondsLeft(ROUND_TIMER_SECONDS);
        const interval = setInterval(() => {
            setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, [battleState?.round, revealing, battleEnd]);

    const makeChoose = (zone) => {
        if (chosen || revealing || battleEnd) return;
        setChosen(zone);
        playChoose();
        if (navigator.vibrate) navigator.vibrate(30);
        socket.send(JSON.stringify({ eventName: 'make.choose', payload: { zone } }));
    };

    const exitGame = () => {
        socket.send(JSON.stringify({ eventName: 'game.stop', payload: player.name }));
    };

    const opponentStatus = () => {
        if (revealing) return <span className="text-cyber-purple tracking-widest">{t('game.revealed')}</span>;
        if (opponentChose) return (
            <span className="text-green-400 animate-pulse tracking-widest">
                {t('game.ready')} <span className="text-green-300">&#10003;</span>
            </span>
        );
        if (chosen) return <span className="text-cyber-cyan animate-pulse tracking-widest">{t('game.choosing')}</span>;
        return <span className="text-cyber-dim tracking-widest">{t('game.standby')}</span>;
    };

    const hpBar = (hp, colorClass) => {
        const pct = Math.max(0, Math.min(100, (hp / INITIAL_HP) * 100));
        const low = hp <= 40;
        const bar = low ? 'bg-cyber-pink' : colorClass;
        return (
            <div className="w-full">
                <div className="flex justify-between items-center text-[10px] sm:text-xs neon-label mb-1">
                    <span>{t('game.battle.hp')}</span>
                    <span className={`font-display font-bold ${low ? 'text-cyber-pink glow-pink' : 'text-white'}`}>{hp}/{INITIAL_HP}</span>
                </div>
                <div className="h-3 sm:h-4 bg-black/60 border border-cyber-border rounded overflow-hidden">
                    <div className={`h-full ${bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
            </div>
        );
    };

    const roleLabel = () => {
        if (!battleState) return '';
        return isAttacker ? t('game.battle.yourRoleAttack') : t('game.battle.yourRoleDefend');
    };

    const choicePrompt = () => {
        if (revealing) return '';
        if (chosen) return t('game.ready');
        return isAttacker ? t('game.battle.chooseAttackZone') : t('game.battle.chooseBlockZone');
    };

    const revealPanel = () => {
        if (!battleRound) return null;
        const iAttacked = battleRound.attackerName === myName;
        const attackZoneLabel = t(`game.battle.${battleRound.attackZone}`);
        const blockZoneLabel = t(`game.battle.${battleRound.blockZone}`);
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-10">
                <div className="text-center space-y-2 px-4">
                    <div className="neon-label">{t('game.battle.round', { n: battleRound.round })}</div>
                    <div className="text-xs sm:text-sm text-cyber-cyan tracking-widest">
                        {battleRound.attackerName} → {attackZoneLabel}
                    </div>
                    <div className="text-xs sm:text-sm text-cyber-purple tracking-widest">
                        {battleRound.defenderName} ⛨ {blockZoneLabel}
                    </div>
                    {battleRound.blocked ? (
                        <div className="text-2xl sm:text-4xl font-display font-black text-cyber-cyan glow-cyan animate-pulse">
                            {t('game.battle.blocked')}
                        </div>
                    ) : (
                        <div className={`text-2xl sm:text-4xl font-display font-black ${iAttacked ? 'text-cyber-purple glow-purple' : 'text-cyber-pink glow-pink'} animate-pulse`}>
                            {t('game.battle.hit', { damage: battleRound.damage })}
                        </div>
                    )}
                    {battleRound.finished && (
                        <div className="text-sm sm:text-base font-display tracking-widest text-cyber-pink glow-pink mt-2">
                            {t('game.battle.knockout')}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const buttonsDisabled = !!chosen || revealing || !!battleEnd || secondsLeft === 0;

    return (
        <div className="h-screen max-h-screen overflow-hidden p-2 sm:p-4 md:p-3">
            <div className="max-w-6xl mx-auto h-full flex flex-col space-y-3 sm:space-y-4 md:space-y-3">
                {/* Header */}
                <div className="neon-panel scanlines p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyber-pink animate-pulse shadow-neon" />
                        <span className="font-display text-xs sm:text-sm tracking-widest text-cyber-pink glow-pink">{t('game.battle.title')}</span>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-center px-3 border-x border-cyber-border">
                            <div className="neon-label text-[10px]">{t('game.bet')}</div>
                            <div className="text-cyber-purple glow-purple font-display font-bold text-sm sm:text-base">{bet}¢</div>
                        </div>
                        <button onClick={exitGame} className="neon-btn neon-btn-pink text-xs">
                            {t('game.abortMatch')}
                        </button>
                    </div>
                </div>

                {/* HP bars + timer + round */}
                <div className="neon-panel p-3 sm:p-4 relative overflow-hidden flex-1 min-h-0 flex flex-col">
                    <div className="grid grid-cols-2 gap-3 sm:gap-6">
                        <div>
                            <div className="neon-label mb-1">{t('game.you')} — <span className="text-cyber-cyan glow-cyan truncate">{myName}</span></div>
                            {hpBar(myHp, 'bg-cyber-cyan')}
                        </div>
                        <div>
                            <div className="neon-label mb-1">{t('game.opponent')} — <span className="text-cyber-pink glow-pink truncate">{opponentName || '...'}</span></div>
                            {hpBar(oppHp, 'bg-cyber-purple')}
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-4 relative">
                        {battleState && !battleEnd && (
                            <>
                                <div className="neon-label">{t('game.battle.round', { n: battleState.round })}</div>
                                <div className={`text-xl sm:text-3xl font-display font-black tracking-widest ${isAttacker ? 'text-cyber-pink glow-pink' : 'text-cyber-cyan glow-cyan'}`}>
                                    {roleLabel()}
                                </div>
                                <div className="text-xs sm:text-sm text-cyber-dim tracking-widest">
                                    {choicePrompt()}
                                </div>
                                {!revealing && !chosen && (
                                    <div className={`font-display text-lg sm:text-2xl ${secondsLeft <= 5 ? 'text-cyber-pink glow-pink animate-pulse' : 'text-cyber-dim'}`}>
                                        {t('game.battle.timer', { seconds: secondsLeft })}
                                    </div>
                                )}
                                <div className="text-xs mt-1">
                                    {t('game.status')}: {opponentStatus()}
                                </div>
                            </>
                        )}
                        {revealPanel()}
                        <GameResultOverlay result={gameResult} bet={bet} visible={showResult} />
                    </div>
                </div>

                {/* Zone buttons */}
                <div className="neon-panel p-3 sm:p-4 flex-shrink-0">
                    <div className="text-center neon-label mb-2 sm:mb-3">
                        {isAttacker ? t('game.battle.chooseAttackZone') : t('game.battle.chooseBlockZone')}
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl mx-auto">
                        {ZONES.map((zone) => {
                            const icon = zone === 'head' ? '👤' : zone === 'body' ? '🛡' : '🦵';
                            const active = chosen === zone;
                            const colorClass = isAttacker ? 'neon-btn-pink' : '';
                            return (
                                <button
                                    key={zone}
                                    onClick={() => makeChoose(zone)}
                                    disabled={buttonsDisabled}
                                    className={`neon-btn ${colorClass} py-4 sm:py-5 text-sm sm:text-lg flex flex-col items-center gap-1 sm:gap-2 ${active ? 'ring-2 ring-cyber-cyan' : ''}`}
                                >
                                    <span className="text-2xl sm:text-3xl">{icon}</span>
                                    <span>{t(`game.battle.${zone}`)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BattleRoom;
